/**
 * The ⌘K overlay (brief 06, B4; `KLQ-20260905-001` §5a).
 *
 * A centred command palette: one box, results grouped Drawings / Tasks / Comments / Levels /
 * Projects / People, top five each, as you type. ↑ ↓ move, Enter opens, ⌘Enter (Ctrl+Enter)
 * opens the full results page, Esc closes.
 *
 * **Mounting is opening**, as with `Drawer`: the caller renders it to open it, so every open
 * starts with an empty box and no stream. Unmounting aborts whatever stream is in flight.
 *
 * **Selection is held by hit key, not by index.** Exact matches render at once and related ones
 * are appended later inside their groups; an index would slide onto another row the moment an
 * append landed above it. See `model.ts`.
 */

import { useNavigate } from '@tanstack/react-router';
import { Search, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

import { EmptyState, InlineError, SkeletonRows } from '@/components';
import { useMessages } from '@/lib/i18n';

import { MAX_QUERY } from './api';
import type { SearchGroupKey, SearchHit } from './api';
import { flatHits, groupViews, hitKey, isEmptyAnswer, PER_GROUP, selectedKey } from './model';
import { searchProblemKey } from './problems';
import { SearchGroup } from './SearchGroup';
import { SearchHitRow } from './SearchHit';
import styles from './Search.module.css';
import { useSearchStream } from './useSearchStream';

export interface SearchOverlayProps {
  readonly onClose: () => void;
}

export function SearchOverlay({ onClose }: SearchOverlayProps) {
  const t = useMessages('search');
  const navigate = useNavigate();
  const listId = useId();
  const optionPrefix = useId();
  const [input, setInput] = useState('');
  const [chosen, setChosen] = useState<string | null>(null);
  const { settled, state } = useSearchStream(input);

  const views = groupViews(state);
  const hits = flatHits(views);
  const selected = selectedKey(views, chosen);
  const optionId = (hit: SearchHit): string => `${optionPrefix}-${hitKey(hit)}`;

  // The close callback through a ref, so the mount effect keeps empty dependencies — see
  // `Drawer`: re-running it would re-capture "the previously focused element" as this dialog.
  const close = useRef(onClose);
  const box = useRef<HTMLInputElement>(null);
  useEffect(() => {
    close.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // The dialog exists to take the next keystroke; focusing anything else first would make ⌘K
    // a two-step gesture.
    box.current?.focus();
    // Esc is heard on `window` in the capture phase and stopped there: ⌘K opens the palette on
    // top of a Drawer, Popover or menu whose own document-level Escape would otherwise close
    // with it and drop its state. Capture runs before any of them, so only the palette closes.
    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      close.current();
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    // The page behind must not scroll under the palette.
    const scroll = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true });
      document.body.style.overflow = scroll;
      previous?.focus();
    };
  }, []);

  // Keep the selected row in view as ↑ ↓ walk a list taller than the panel.
  useEffect(() => {
    if (selected === null) return;
    document.getElementById(`${optionPrefix}-${selected}`)?.scrollIntoView({ block: 'nearest' });
  }, [optionPrefix, selected]);

  const open = (hit: SearchHit): void => {
    onClose();
    void navigate({ href: hit.route });
  };

  const openAll = (group?: SearchGroupKey): void => {
    if (settled === '') return;
    onClose();
    void navigate({
      to: '/search',
      search: { q: settled, ...(group === undefined ? {} : { type: group }) },
    });
  };

  const move = (step: 1 | -1): void => {
    if (hits.length === 0) return;
    const at = hits.findIndex((hit) => hitKey(hit) === selected);
    const next = hits[(at + step + hits.length) % hits.length];
    if (next !== undefined) setChosen(hitKey(next));
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    // An Enter that confirms an IME composition (a diacritic, a CJK word) is not "open".
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      move(event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (event.metaKey || event.ctrlKey) {
        openAll();
        return;
      }
      const hit = hits.find((one) => hitKey(one) === selected);
      if (hit !== undefined) open(hit);
    }
  };

  let body: ReactNode;
  if (state.phase === 'idle') {
    body = <p className={styles.hint}>{t('overlay.hint')}</p>;
  } else if (state.phase === 'waiting') {
    body = (
      <div className={styles.waiting}>
        <SkeletonRows label={t('page.loading')} rows={3} columns={[16, '56%']} rowHeight={44} />
      </div>
    );
  } else if (isEmptyAnswer(state, views)) {
    body = (
      <div className={styles.empty}>
        <EmptyState
          variant="screen"
          // TODO(-014): placeholder sentence in the brief's voice; wave 3 writes the real one.
          explains={t('empty.explains')}
          title={t('empty.title', { q: settled })}
          body={t('empty.body')}
        />
      </div>
    );
  } else {
    body = views.map((view) => (
      <SearchGroup
        key={view.group}
        role="group"
        group={view.group}
        lexical={view.lexical}
        semantic={view.semantic}
        pending={view.pending}
        action={
          view.lexical.length >= PER_GROUP ? (
            <button
              type="button"
              className={styles.groupLink}
              onClick={() => {
                openAll(view.group);
              }}
            >
              {t('group.showAll')}
            </button>
          ) : undefined
        }
        renderHit={(hit) => (
          <SearchHitRow
            hit={hit}
            optionId={optionId(hit)}
            selected={hitKey(hit) === selected}
            onOpen={open}
            onPoint={() => {
              if (hitKey(hit) !== selected) setChosen(hitKey(hit));
            }}
          />
        )}
      />
    ));
  }

  const activeHit = hits.find((hit) => hitKey(hit) === selected);

  return (
    <div className={styles.overlay}>
      {/* `mousedown`, not `click`, for `Drawer`'s reason: a drag that starts in the box and
          overshoots onto the scrim is not a dismissal. */}
      <div
        className={styles.scrim}
        role="presentation"
        onMouseDown={() => {
          onClose();
        }}
      />
      <div className={styles.palette} role="dialog" aria-modal="true" aria-label={t('overlay.title')}>
        <div className={styles.boxRow}>
          <Search className={styles.boxIcon} size={16} strokeWidth={1.5} aria-hidden />
          {/* A bare input rather than `Input`: the palette's box is the dialog's head, borderless
              and full-width, and `Input`'s label-over-field frame is exactly what it is not.
              16px, which is also the size under which a phone zooms on focus. */}
          <input
            className={styles.box}
            type="search"
            role="combobox"
            aria-label={t('overlay.label')}
            aria-expanded={hits.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeHit === undefined ? undefined : optionId(activeHit)}
            placeholder={t('overlay.placeholder')}
            maxLength={MAX_QUERY}
            autoComplete="off"
            spellCheck={false}
            ref={box}
            value={input}
            onChange={(event) => {
              setInput(event.target.value);
            }}
            onKeyDown={onInputKeyDown}
          />
          <button type="button" className={styles.closeButton} onClick={onClose}>
            <X size={16} strokeWidth={1.5} aria-hidden />
            <span className="sr-only">{t('overlay.close')}</span>
          </button>
        </div>

        <div className={styles.results} id={listId} role="listbox" aria-label={t('overlay.title')}>
          {body}
          {state.problem === null ? null : (
            <div className={styles.problem}>
              <InlineError variant="block">{t(searchProblemKey(state.problem))}</InlineError>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          {state.semanticUnavailable ? (
            <span className={styles.footerNote}>{t('unavailable')}</span>
          ) : (
            <span className={styles.footerKeys} aria-hidden>
              {t('overlay.keys')}
            </span>
          )}
          <button
            type="button"
            className={styles.groupLink}
            disabled={settled === ''}
            onClick={() => {
              openAll();
            }}
          >
            {t('overlay.showAll')}
          </button>
        </div>
      </div>
    </div>
  );
}
